import React, { useEffect, useState } from "react";
import { api, brl, formatDate } from "../../lib/api";
import { toast } from "sonner";
import { Wallet, TrendingUp, ShieldAlert, RotateCcw, Sliders, Clock, ArrowDownCircle, X, PiggyBank, CheckCircle2, XCircle, Loader2 } from "lucide-react";

const PURPLE = "#2B1E4A";
const GOLD = "#F5B400";

const LEDGER_LABEL = {
  COMMISSION_EARNED: ["Comissao", "text-emerald-600"],
  COMMISSION_REVERSAL: ["Estorno de comissao", "text-red-600"],
  RESPONSIBILITY_DEBIT: ["Responsabilidade", "text-red-600"],
  RESPONSIBILITY_REFUND: ["Recuperacao", "text-emerald-600"],
  MANUAL_CREDIT: ["Credito manual", "text-emerald-600"],
  MANUAL_DEBIT: ["Debito manual", "text-red-600"],
  WITHDRAWAL_RESERVED: ["Reserva de saque", "text-red-600"],
  WITHDRAWAL_RELEASED: ["Liberacao de reserva", "text-emerald-600"],
  WITHDRAWAL_PAID: ["Saque pago", "text-[#5B2CA0]"],
};
const WD_STATUS = {
  solicitado: ["Solicitado", "bg-amber-100 text-amber-700"],
  em_analise: ["Em analise", "bg-blue-100 text-blue-700"],
  aprovado: ["Aprovado", "bg-indigo-100 text-indigo-700"],
  pago: ["Pago", "bg-emerald-100 text-emerald-700"],
  rejeitado: ["Rejeitado", "bg-red-100 text-red-700"],
  cancelado: ["Cancelado", "bg-gray-100 text-gray-500"],
};

export default function EletrosContaCorrente() {
  const [sum, setSum] = useState(null);
  const [statement, setStatement] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [tab, setTab] = useState("conta");
  const [reqModal, setReqModal] = useState(false);
  const [pixModal, setPixModal] = useState(false);

  const load = async () => {
    const [s, st, wd] = await Promise.all([
      api.get("/eletros/account/summary"),
      api.get("/eletros/account/statement"),
      api.get("/eletros/withdrawals"),
    ]);
    setSum(s.data); setStatement(st.data); setWithdrawals(wd.data);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  if (!sum) return <div className="p-6 text-sm text-[#5C5A57]">Carregando...</div>;

  const canWithdraw = sum.withdrawable >= sum.withdraw_min && sum.window.is_open;

  return (
    <div className="space-y-6 p-4 lg:p-8" data-testid="eletros-conta-page">
      <div className="flex flex-wrap items-center gap-3 rounded-2xl p-5 text-white" style={{ background: `linear-gradient(135deg, ${PURPLE}, #5B2CA0)` }}>
        <div className="grid h-12 w-12 place-items-center rounded-xl" style={{ background: GOLD }}><PiggyBank size={26} className="text-[#2B1E4A]" /></div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: GOLD }}>Eletros</div>
          <h1 className="font-display text-xl font-bold">Conta corrente</h1>
          <p className="text-[12px] text-white/70">Proxima janela de saque: <b>{sum.window.label}</b> ({formatDate(sum.window.next_date)}) · minimo {brl(sum.withdraw_min)}</p>
        </div>
        <button onClick={() => setReqModal(true)} disabled={!canWithdraw} data-testid="eletros-request-withdraw-btn"
          className="flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold text-[#2B1E4A] disabled:opacity-50" style={{ background: GOLD }}>
          <ArrowDownCircle size={16} /> Solicitar saque
        </button>
      </div>

      {/* Cards financeiros */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Card icon={TrendingUp} label="Comissao gerada" value={brl(sum.commission_generated)} />
        <Card icon={ShieldAlert} label="Responsabilidades" value={brl(sum.responsibility_pending)} danger={sum.responsibility_pending > 0} />
        <Card icon={RotateCcw} label="Creditos recuperacao" value={brl(sum.recovery_credits)} />
        <Card icon={Sliders} label="Ajustes" value={brl(sum.manual_adjustments)} />
        <Card icon={Wallet} label="Saldo liquido" value={brl(sum.net_balance)} negative={sum.net_balance < 0} />
        <Card icon={PiggyBank} label="Disponivel p/ saque" value={brl(sum.withdrawable)} gold negative={sum.withdrawable < 0} />
        <Card icon={Loader2} label="Em processamento" value={brl(sum.in_processing)} />
        <Card icon={CheckCircle2} label="Total sacado" value={brl(sum.total_withdrawn)} />
      </div>

      {!canWithdraw && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-[13px] text-amber-800" data-testid="eletros-withdraw-hint">
          {!sum.window.is_open ? `Fora da janela de saque. Proximo saque disponivel em ${formatDate(sum.window.next_date)}.`
            : `Saldo disponivel (${brl(sum.withdrawable)}) abaixo do minimo (${brl(sum.withdraw_min)}).`}
        </div>
      )}

      {/* PIX */}
      <div className="flex items-center justify-between rounded-2xl border border-border bg-white p-4" data-testid="eletros-payout-box">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#9A8A98]">Dados de recebimento (PIX)</div>
          <div className="font-semibold text-brand-dark">{sum.payout ? `${sum.payout.holder_name} · ${sum.payout.masked_key}` : "Nao cadastrado"}</div>
        </div>
        <button onClick={() => setPixModal(true)} data-testid="eletros-edit-payout-btn" className="rounded-xl border border-border px-4 py-2 text-sm font-semibold text-[#5B2CA0]">{sum.payout ? "Alterar" : "Cadastrar"}</button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        {[["conta", "Extrato"], ["saques", "Meus saques"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`eletros-tab-${k}`}
            className={`rounded-xl px-4 py-2 text-sm font-semibold ${tab === k ? "text-white" : "border border-border text-[#6B5A69]"}`}
            style={tab === k ? { background: PURPLE } : {}}>{l}</button>
        ))}
      </div>

      {tab === "conta" ? (
        <div className="rounded-2xl border border-border bg-white p-5" data-testid="eletros-statement">
          <h2 className="mb-3 font-display text-base font-bold text-brand-dark">Extrato da conta corrente</h2>
          {statement.length === 0 ? <div className="py-6 text-center text-sm text-[#9A8A98]">Sem lancamentos ainda.</div> : (
            <div className="space-y-1.5">
              {statement.map((r) => {
                const [label, cls] = LEDGER_LABEL[r.type] || [r.type, "text-brand-dark"];
                return (
                  <div key={r.id} className="flex items-center justify-between gap-3 border-b border-border/50 py-2 text-[13px]" data-testid={`stmt-row-${r.id}`}>
                    <div className="min-w-0">
                      <div className="font-medium text-brand-dark">{label}{r.client_name ? ` · ${r.client_name}` : ""}</div>
                      <div className="text-[11px] text-[#9A8A98]">{formatDate(r.created_at)}</div>
                    </div>
                    <div className="text-right">
                      <div className={`font-semibold ${cls}`}>{r.amount >= 0 ? "+ " : "- "}{brl(Math.abs(r.amount))}</div>
                      <div className="text-[11px] text-[#9A8A98]">saldo {brl(r.balance_after)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-2xl border border-border bg-white p-5" data-testid="eletros-my-withdrawals">
          <h2 className="mb-3 font-display text-base font-bold text-brand-dark">Meus saques</h2>
          {withdrawals.length === 0 ? <div className="py-6 text-center text-sm text-[#9A8A98]">Nenhum saque solicitado.</div> : (
            <div className="space-y-2">
              {withdrawals.map((w) => {
                const [label, cls] = WD_STATUS[w.status] || [w.status, "bg-gray-100"];
                return (
                  <div key={w.id} className="flex items-center justify-between gap-3 rounded-xl border border-border p-3" data-testid={`wd-row-${w.id}`}>
                    <div className="min-w-0">
                      <div className="font-semibold text-brand-dark">{brl(w.amount)}</div>
                      <div className="text-[12px] text-[#8A7788]">{formatDate(w.created_at)} · {w.pix?.masked_key}</div>
                      {w.reject_reason && <div className="text-[11px] text-red-600">Motivo: {w.reject_reason}</div>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase ${cls}`}>{label}</span>
                      {["solicitado", "em_analise"].includes(w.status) && (
                        <button onClick={async () => { try { await api.post(`/eletros/withdrawals/${w.id}/cancel`); toast.success("Saque cancelado"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Erro"); } }}
                          data-testid={`wd-cancel-${w.id}`} className="grid h-7 w-7 place-items-center rounded-lg border border-border text-[#6B5A69]"><X size={14} /></button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {reqModal && <RequestModal sum={sum} onClose={() => setReqModal(false)} onDone={() => { setReqModal(false); load(); }} />}
      {pixModal && <PixModal current={sum.payout} onClose={() => setPixModal(false)} onDone={() => { setPixModal(false); load(); }} />}
    </div>
  );
}

function RequestModal({ sum, onClose, onDone }) {
  const [amount, setAmount] = useState(sum.withdraw_full_required ? sum.withdrawable : "");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      const body = sum.withdraw_full_required ? {} : { amount: Number(amount) };
      await api.post("/eletros/withdrawals", body);
      toast.success("Saque solicitado!");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao solicitar"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/50 p-6" onClick={onClose}>
      <div className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-pop" onClick={(e) => e.stopPropagation()} data-testid="eletros-withdraw-modal">
        <h2 className="font-display text-lg font-bold text-brand-dark">Solicitar saque</h2>
        <div className="mt-2 rounded-xl bg-muted/50 p-3 text-sm">
          <div className="flex justify-between"><span className="text-[#8A7788]">Disponivel</span><span className="font-bold text-brand-dark">{brl(sum.withdrawable)}</span></div>
          <div className="flex justify-between"><span className="text-[#8A7788]">PIX</span><span className="font-medium text-brand-dark">{sum.payout?.masked_key}</span></div>
        </div>
        {sum.withdraw_full_required ? (
          <p className="mt-3 text-sm text-[#6B5A69]">Saque total obrigatorio: sera solicitado <b>{brl(sum.withdrawable)}</b>.</p>
        ) : (
          <label className="mt-3 block"><span className="mb-1 block text-[10px] uppercase tracking-widest text-[#9A8A98]">Valor (min {brl(sum.withdraw_min)})</span>
            <input type="number" step="0.01" className="inp" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="eletros-withdraw-amount" /></label>
        )}
        <div className="mt-5 flex gap-2">
          <button onClick={submit} disabled={saving} data-testid="eletros-withdraw-confirm" className="flex-1 rounded-xl py-3 text-sm font-semibold text-white disabled:opacity-60" style={{ background: PURPLE }}>{saving ? "..." : "Confirmar"}</button>
          <button onClick={onClose} className="rounded-xl border border-border px-4 py-3 text-sm font-semibold text-[#6B5A69]">Cancelar</button>
        </div>
      </div>
    </div>
  );
}

function PixModal({ current, onClose, onDone }) {
  const [f, setF] = useState({ key_type: current?.key_type || "cpf", key: "", holder_name: current?.holder_name || "", holder_document: "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.key.trim() || !f.holder_name.trim()) { toast.error("Chave e favorecido obrigatorios"); return; }
    setSaving(true);
    try { await api.put("/eletros/payout", f); toast.success("Dados PIX salvos"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/50 p-6" onClick={onClose}>
      <div className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-pop" onClick={(e) => e.stopPropagation()} data-testid="eletros-pix-modal">
        <h2 className="font-display text-lg font-bold text-brand-dark">Dados de recebimento (PIX)</h2>
        <div className="mt-3 space-y-3">
          <label className="block"><span className="mb-1 block text-[10px] uppercase tracking-widest text-[#9A8A98]">Tipo de chave</span>
            <select className="inp" value={f.key_type} onChange={(e) => setF({ ...f, key_type: e.target.value })} data-testid="pix-key-type">
              <option value="cpf">CPF</option><option value="cnpj">CNPJ</option><option value="email">E-mail</option><option value="telefone">Telefone</option><option value="aleatoria">Aleatoria</option>
            </select></label>
          <label className="block"><span className="mb-1 block text-[10px] uppercase tracking-widest text-[#9A8A98]">Chave PIX</span>
            <input className="inp" value={f.key} onChange={(e) => setF({ ...f, key: e.target.value })} placeholder={current ? `Atual: ${current.masked_key}` : ""} data-testid="pix-key" /></label>
          <label className="block"><span className="mb-1 block text-[10px] uppercase tracking-widest text-[#9A8A98]">Nome do favorecido</span>
            <input className="inp" value={f.holder_name} onChange={(e) => setF({ ...f, holder_name: e.target.value })} data-testid="pix-holder" /></label>
          <label className="block"><span className="mb-1 block text-[10px] uppercase tracking-widest text-[#9A8A98]">CPF/CNPJ do favorecido</span>
            <input className="inp" value={f.holder_document} onChange={(e) => setF({ ...f, holder_document: e.target.value })} data-testid="pix-doc" /></label>
        </div>
        <div className="mt-5 flex gap-2">
          <button onClick={submit} disabled={saving} data-testid="pix-save" className="flex-1 rounded-xl py-3 text-sm font-semibold text-white disabled:opacity-60" style={{ background: PURPLE }}>{saving ? "..." : "Salvar"}</button>
          <button onClick={onClose} className="rounded-xl border border-border px-4 py-3 text-sm font-semibold text-[#6B5A69]">Cancelar</button>
        </div>
      </div>
    </div>
  );
}

function Card({ icon: Icon, label, value, gold, danger, negative }) {
  const color = negative || danger ? "#DC2626" : gold ? "#C9960A" : PURPLE;
  return (
    <div className="rounded-2xl border border-border bg-white p-4" data-testid={`conta-card-${label}`}>
      <Icon size={17} className={danger || negative ? "text-red-500" : gold ? "text-[#C9960A]" : "text-[#5B2CA0]"} />
      <div className="mt-2 text-[10px] uppercase tracking-widest text-[#9A8A98]">{label}</div>
      <div className="font-display text-base font-bold" style={{ color }}>{value}</div>
    </div>
  );
}
